import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, ChevronDown, Download, Eraser, Hand, Paintbrush, Pencil, Pipette, RotateCcw, Type, X, ZoomIn, ZoomOut } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { imageApi, usageApi, xhsApi, galleryApi } from '../api/client'
import { BRAND_OPTIONS, PREMIUM_LIMITS, findBrandOption, getBrandBaseId, getDefaultBeadSize, gridToCm } from '../utils/beadData'
import { hexToRgb } from '../utils/color'
import {
  generateBeadPattern,
  remapPatternBrand,
  renderPatternToDataURL,
} from '../utils/imageProcessor'
import {
  createPatternWatermarkCanvas,
  exportPatternPng,
  exportPatternPdf,
  downloadDataUrl,
  downloadBlob,
} from '../utils/exportPattern'
import { IRONING_EFFECTS, renderIroningPreview } from '../utils/ironingRenderer'
import storage from '../utils/storage'
import { getWorkspaceDraft, saveWorkspaceDraft, removeWorkspaceDraft } from '../utils/workspaceDraft'
import BeadEditor from '../components/BeadEditor'
import TextBeadModal from '../components/TextBeadModal'
import TextOverlayModal from '../components/TextOverlayModal'
import { setLocalBeadModePattern } from './BeadModePage'
import ImageCropModal from '../components/ImageCropModal'

const CANVAS_SCALE = 16 // 每个格子渲染像素数
const MIN_CELL_FOR_CODE = 16 // 格子大于等于此尺寸才显示色号
const BRUSH_SIZES = [1, 3, 5]
const MIN_CANVAS_ZOOM = 1
const MAX_CANVAS_ZOOM = 4
const MAX_LOCAL_IMAGE_BYTES = 25 * 1024 * 1024
const SUPPORTED_LOCAL_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp'])
const SUPPORTED_LOCAL_IMAGE_NAME = /\.(?:jpe?g|png|webp)$/i

// AI 生图等待时的小贴士（内置默认值；管理员可在后台自定义覆盖）
const AI_LOADING_TIPS = [
  // 熨烫
  '🔥 熨烫垫烘焙纸',
  '🔥 低温少量多次',
  '🔥 烫完压书定型',
  '🔥 大图正反各烫',
  '🔥 毛巾隔层起绒',
  // 色彩搭配
  '🎨 深色描边更清晰',
  '🎨 阴影用相邻色',
  '🎨 肤色选浅米系',
  '🎨 少色更干净',
  '🎨 撞色更出挑',
  // 网站功能
  '✨ 可预览熨烫效果',
  '✨ 吸附取色零色差',
  '✨ 图例按单买豆',
  '✨ 签到送 AI 额度',
  '✨ 分享码发好友',
  // 小鼻嘎与大图
  '🧩 小鼻嘎 29×29',
  '🧩 挂件留加固位',
  '🧩 大图先拼轮廓',
  '🧩 按 5×5 分区拼',
  '🧩 新手选 5mm 豆',
]

function dataURItoFile(dataURI, filename = 'image.png') {
  const byteString = atob(dataURI.split(',')[1])
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
  const ab = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(ab)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  return new File([ab], filename, { type: mimeString })
}

export default function Home() {
  const { user, isPremium, isPermanent, refreshUsage } = useAuth()
  const limits = isPremium ? PREMIUM_LIMITS.premium : PREMIUM_LIMITS.free
  // AI 生图：会员消耗额度；免费用户每日免费 1 张（服务端强制限制）
  const canUseAi = isPremium
    ? (user?.quota || 0) > 0 && (!user?.quotaExpiresAt || user.quotaExpiresAt > Date.now())
    : true

  // AI 额度过期日期展示
  const quotaExpiresText = useMemo(() => {
    const ts = user?.quotaExpiresAt
    if (!ts || ts <= 0) return ''
    const d = new Date(ts)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [user?.quotaExpiresAt])

  const [brandId, setBrandId] = useState(BRAND_OPTIONS[0].id)
  const [boardSize, setBoardSize] = useState(50)
  const [beadSize, setBeadSize] = useState(() => getDefaultBeadSize(getBrandBaseId(BRAND_OPTIONS[0].id)))
  const [patternTitle, setPatternTitle] = useState('')
  // 导出时分享选择：public 公开 / private 仅自己可见 / none 不保存
  const [saveMode, setSaveMode] = useState('public')
  // 导出镜像：默认非镜像，开启后 PNG/PDF 图纸水平翻转
  const [mirrorExport, setMirrorExport] = useState(false)
  const [image, setImage] = useState(null)
  // 图片来源：ai / upload / link / ai-pixel，用于决定转换管线
  const [imageSource, setImageSource] = useState(null)
  // AI 输出仍需经过拼豆量化与边缘保护；仅原生像素画才走高还原管线。
  const isAiGenerated = imageSource === 'ai'
  const faithful = imageSource === 'ai-pixel'
  const [pattern, setPattern] = useState(null) // { grid: [[colorId]], legend: {}, width, height, total }
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiStyle, setAiStyle] = useState('')
  const [aiTask, setAiTask] = useState(null) // { taskId, status, imageUrl }
  const [aiLoading, setAiLoading] = useState(false)
  // 生图等待小贴士轮播
  const [aiTipIndex, setAiTipIndex] = useState(0)
  // 小贴士内容：优先用管理员后台自定义的配置，未配置时用内置默认
  const [aiTips, setAiTips] = useState(AI_LOADING_TIPS)
  useEffect(() => {
    imageApi.tips()
      .then(({ data }) => {
        if (Array.isArray(data.tips) && data.tips.length > 0) setAiTips(data.tips)
      })
      .catch(() => {})
  }, [])
  // AI 参考图（风格，最多 1 张）与垫图（内容，最多 4 张）
  const [aiStyleRef, setAiStyleRef] = useState(null) // { id, src } | null
  const [aiBaseImages, setAiBaseImages] = useState([]) // [{ id, src }]
  const [aiUploading, setAiUploading] = useState(false)
  const [textModalOpen, setTextModalOpen] = useState(false)
  const [overlayModalOpen, setOverlayModalOpen] = useState(false)
  const navigate = useNavigate()
  // 当前图纸若来自文字弹窗，记录其配置用于“重新编辑文字”
  const [textSource, setTextSource] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [imageReading, setImageReading] = useState(false)
  const [imageUploadError, setImageUploadError] = useState('')

  // 小红书导入
  const [xhsUrl, setXhsUrl] = useState('')
  const [xhsLoading, setXhsLoading] = useState(false)
  const [xhsImages, setXhsImages] = useState([])
  const [xhsTitle, setXhsTitle] = useState('')
  const [showXhsDisclaimer, setShowXhsDisclaimer] = useState(false)

  // 高级选项
  const [maxColors, setMaxColors] = useState(36)
  const [isCustomColorCount, setIsCustomColorCount] = useState(false)
  // 图像优化强度：0-100 无级调节，0/50/100 对应弱/标准/强三档
  const [optimizeValue, setOptimizeValue] = useState(0)
  // 自动描边：默认关闭（只对主体外轮廓勾勒，需要时手动开启）
  const [outline, setOutline] = useState(false)
  // 抖动适合照片/渐变，默认关闭以避免图纸散点过多
  const [dithering, setDithering] = useState(false)
  // 普通导入图默认清除从画布边缘连通的近白背景
  const [removeBackground, setRemoveBackground] = useState(true)
  // 普通图片自动优化：细节模式（五官不糊）+ 轻度增鲜 + 杂色清理
  // 注意：以下派生量依赖上面的 state，必须声明在 state 之后，否则会触发 TDZ 错误导致白屏
  // 合色只收拢肉眼难辨的近似色，避免肤色阴影和五官层次被全局主色吞并。
  const OPTIMIZE_LABEL = optimizeValue < 25 ? '弱 · 保留细节' : optimizeValue < 75 ? '标准 · 推荐' : '强 · 更干净'
  const effMerge = faithful
    ? 0
    : optimizeValue <= 50
      ? 0.008 + (optimizeValue / 50) * 0.022
      : 0.03 + ((optimizeValue - 50) / 50) * 0.025
  const effDenoise = faithful ? 0 : optimizeValue >= 70 ? 2 : 1
  const effPixelMode = faithful ? 'dominant' : 'detail'
  const effOutline = faithful ? false : outline
  const effDithering = faithful ? false : dithering
  const effSaturation = faithful ? 1 : 1 + (optimizeValue / 100) * 0.1
  const effPreprocessStrength = faithful ? 0 : optimizeValue / 100
  const effRemoveBackground = faithful ? false : removeBackground
  const [showGrid, setShowGrid] = useState(true)
  const [showGroupLines, setShowGroupLines] = useState(true)
  const [showColorCodes, setShowColorCodes] = useState(true)
  const [paramsLocked, setParamsLocked] = useState(false)
  // 锁定后由编辑器实时上报的画幅规格（随缩放变化）
  const [liveGridSize, setLiveGridSize] = useState(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isCellEditing, setIsCellEditing] = useState(false)
  const beadEditorRef = useRef(null)
  // 三步流程：1 选择原图 / 2 编辑调整 / 3 生成导出
  const [step, setStep] = useState(1)

  // === 编辑图纸模式 ===
  const [editMode, setEditMode] = useState(false)
  const [editedPattern, setEditedPattern] = useState(null)
  const [selectedColorId, setSelectedColorId] = useState(null)
  // 编辑工具：画笔 / 吸色 / 橡皮擦
  const [pickColorMode, setPickColorMode] = useState(false)
  const [eraseMode, setEraseMode] = useState(false)
  const [brushSize, setBrushSize] = useState(1)
  const [panMode, setPanMode] = useState(false)
  const [canvasZoom, setCanvasZoom] = useState(1)
  const [batchSourceColorId, setBatchSourceColorId] = useState('')
  const [showPatternColorTools, setShowPatternColorTools] = useState(false)
  // 编辑历史（单步撤回）与草稿箱（自动保存）
  const [editHistory, setEditHistory] = useState([])
  const [draft, setDraft] = useState(null)
  const [editorState, setEditorState] = useState(null)
  // 会员水印
  const [userWatermark, setUserWatermark] = useState('')
  const [enableWatermark, setEnableWatermark] = useState(false)

  // 预览效果：pattern 图纸 / 熨烫效果（半熔、全熔、闪粉烫、毛巾烫…）
  const [previewEffect, setPreviewEffect] = useState('pattern')

  const previewCanvasRef = useRef(null)
  const paintStrokeRef = useRef(null)
  const canvasViewportRef = useRef(null)
  const activePointersRef = useRef(new Map())
  const pinchGestureRef = useRef(null)
  const panGestureRef = useRef(null)
  const suppressTouchRef = useRef(false)
  const touchToolSnapshotRef = useRef(null)
  const canvasZoomRef = useRef(1)
  canvasZoomRef.current = canvasZoom
  const fileInputRef = useRef(null)
  const aiStyleRefInputRef = useRef(null)
  const aiBaseInputRef = useRef(null)
  const pollIntervalRef = useRef(null)

  // 草稿箱 key（按用户隔离，未登录用 guest）
  const draftKey = `pindou_draft_${user?.id || 'guest'}`

  const workspaceSnapshot = useMemo(() => {
    const isActiveWorkspace = (step === 2 && image) || (step === 3 && pattern)
    if (!isActiveWorkspace) return null

    return {
      version: 3,
      step,
      image,
      imageSource,
      pattern,
      editedPattern,
      editMode,
      editorState,
      brandId,
      boardSize,
      beadSize,
      patternTitle,
      saveMode,
      mirrorExport,
      maxColors,
      isCustomColorCount,
      optimizeValue,
      outline,
      dithering,
      removeBackground,
      showGrid,
      showGroupLines,
      showColorCodes,
      paramsLocked,
      liveGridSize,
      selectedColorId,
      pickColorMode,
      eraseMode,
      brushSize,
      panMode,
      canvasZoom,
      batchSourceColorId,
      userWatermark,
      enableWatermark,
      previewEffect,
      textSource,
      savedAt: Date.now(),
    }
  }, [
    step, image, imageSource, pattern, editedPattern, editMode, editorState,
    brandId, boardSize, beadSize, patternTitle, saveMode, mirrorExport,
    maxColors, isCustomColorCount, optimizeValue, outline, dithering,
    removeBackground, showGrid, showGroupLines, showColorCodes, paramsLocked,
    liveGridSize, selectedColorId, pickColorMode, eraseMode, brushSize, panMode, canvasZoom,
    batchSourceColorId,
    userWatermark, enableWatermark, previewEffect, textSource,
  ])

  const latestWorkspaceRef = useRef(null)
  latestWorkspaceRef.current = workspaceSnapshot ? { key: draftKey, draft: workspaceSnapshot } : null

  // 加载草稿；IndexedDB 可容纳第二步中的大尺寸 data URL 图片。
  useEffect(() => {
    let active = true
    getWorkspaceDraft(draftKey)
      .then((savedDraft) => {
        if (active) setDraft(savedDraft)
      })
      .catch(() => {
        if (active) setDraft(null)
      })
    return () => {
      active = false
    }
  }, [draftKey])

  // 第二、三步任意状态变化均自动保存，拖拽/缩放时防抖以减少写盘。
  useEffect(() => {
    if (!workspaceSnapshot) return
    let active = true
    const timer = setTimeout(async () => {
      const saved = await saveWorkspaceDraft(draftKey, workspaceSnapshot)
      if (active && saved) setDraft(workspaceSnapshot)
      if (!saved) console.warn('[draft] workspace could not be persisted')
    }, 350)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [draftKey, workspaceSnapshot])

  // 路由切换会卸载 Home，立即写入最新快照，避免防抖尚未结束时丢失。
  useEffect(() => () => {
    const latest = latestWorkspaceRef.current
    if (latest) saveWorkspaceDraft(latest.key, latest.draft)
  }, [])

  // 依据 grid 重新计算色号图例与总豆数
  const recomputePatternStats = (grid) => {
    const h = grid.length
    const w = grid[0]?.length || 0
    const legend = new Map()
    let total = 0
    for (let ry = 0; ry < h; ry++) {
      for (let rx = 0; rx < w; rx++) {
        const id = grid[ry][rx]
        const color = brand.colors.find((c) => c.id === id)
        if (!color) continue
        const entry = legend.get(id) || { ...color, count: 0 }
        entry.count += 1
        legend.set(id, entry)
        total += 1
      }
    }
    return { legend: Array.from(legend.values()), total }
  }

  // 单步撤回：恢复上一版 grid
  const undoEdit = () => {
    if (!editHistory.length || !editedPattern) return
    const lastGrid = editHistory[editHistory.length - 1]
    const { legend, total } = recomputePatternStats(lastGrid)
    setEditedPattern({ ...editedPattern, grid: lastGrid, legend, total })
    if (!legend.some((item) => item.id === batchSourceColorId)) {
      setBatchSourceColorId(legend[0]?.id || '')
    }
    setEditHistory((prev) => prev.slice(0, -1))
  }

  const applyEditedGrid = (newGrid) => {
    if (!editedPattern) return
    setEditHistory((prev) => [...prev.slice(-49), editedPattern.grid])
    const { legend, total } = recomputePatternStats(newGrid)
    setEditedPattern({ ...editedPattern, grid: newGrid, legend, total })
  }
  const clampCanvasZoom = (zoom) => Math.min(MAX_CANVAS_ZOOM, Math.max(MIN_CANVAS_ZOOM, zoom))

  const setCanvasZoomAt = (nextZoom, clientX, clientY) => {
    const viewport = canvasViewportRef.current
    const currentZoom = canvasZoomRef.current
    const zoom = clampCanvasZoom(nextZoom)
    if (!viewport || zoom === currentZoom) return

    const rect = viewport.getBoundingClientRect()
    const focalX = clientX ?? rect.left + rect.width / 2
    const focalY = clientY ?? rect.top + rect.height / 2
    const contentX = (viewport.scrollLeft + focalX - rect.left) / currentZoom
    const contentY = (viewport.scrollTop + focalY - rect.top) / currentZoom

    canvasZoomRef.current = zoom
    setCanvasZoom(zoom)
    requestAnimationFrame(() => {
      viewport.scrollLeft = contentX * zoom - (focalX - rect.left)
      viewport.scrollTop = contentY * zoom - (focalY - rect.top)
    })
  }

  const getPatternCellFromPointer = (event) => {
    const canvas = previewCanvasRef.current
    if (!canvas || !editedPattern) return null
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = Math.floor(((event.clientX - rect.left) * scaleX) / CANVAS_SCALE)
    const y = Math.floor(((event.clientY - rect.top) * scaleY) / CANVAS_SCALE)
    if (x < 0 || x >= editedPattern.width || y < 0 || y >= editedPattern.height) return null
    return { x, y }
  }

  const paintStrokeAt = (event) => {
    const stroke = paintStrokeRef.current
    const cell = getPatternCellFromPointer(event)
    if (!stroke || !cell || stroke.pointerId !== event.pointerId) return
    if (stroke.lastCell?.x === cell.x && stroke.lastCell?.y === cell.y) return

    const from = stroke.lastCell || cell
    const dx = cell.x - from.x
    const dy = cell.y - from.y
    const steps = Math.max(Math.abs(dx), Math.abs(dy), 1)
    const radius = Math.floor(stroke.size / 2)
    let changed = false

    for (let step = 0; step <= steps; step++) {
      const centerX = Math.round(from.x + (dx * step) / steps)
      const centerY = Math.round(from.y + (dy * step) / steps)
      for (let y = centerY - radius; y <= centerY + radius; y++) {
        if (y < 0 || y >= editedPattern.height) continue
        for (let x = centerX - radius; x <= centerX + radius; x++) {
          if (x < 0 || x >= editedPattern.width || stroke.grid[y][x] === stroke.nextId) continue
          stroke.grid[y][x] = stroke.nextId
          changed = true
        }
      }
    }
    stroke.lastCell = cell
    if (!changed) return

    stroke.changed = true
    const nextGrid = stroke.grid.map((row) => [...row])
    const { legend, total } = recomputePatternStats(nextGrid)
    setEditedPattern((current) => current ? { ...current, grid: nextGrid, legend, total } : current)
  }

  const cancelPaintStroke = (revert = false) => {
    const stroke = paintStrokeRef.current
    paintStrokeRef.current = null
    if (!revert || !stroke?.changed) return
    const { legend, total } = recomputePatternStats(stroke.originalGrid)
    setEditedPattern((current) => current
      ? { ...current, grid: stroke.originalGrid, legend, total }
      : current)
  }

  const startPaintStroke = (event) => {
    if (!editedPattern || (!pickColorMode && !eraseMode && !selectedColorId)) return
    const cell = getPatternCellFromPointer(event)
    if (!cell) return

    if (pickColorMode) {
      const pickedId = editedPattern.grid[cell.y][cell.x]
      setPickColorMode(false)
      if (pickedId) {
        setSelectedColorId(pickedId)
        setEraseMode(false)
      } else {
        setEraseMode(true)
      }
      return
    }

    paintStrokeRef.current = {
      pointerId: event.pointerId,
      originalGrid: editedPattern.grid,
      grid: editedPattern.grid.map((row) => [...row]),
      nextId: eraseMode ? null : selectedColorId,
      size: brushSize,
      changed: false,
      lastCell: null,
    }
    paintStrokeAt(event)
  }

  const finishPaintStroke = (pointerId) => {
    const stroke = paintStrokeRef.current
    if (!stroke || stroke.pointerId !== pointerId) return
    if (stroke.changed) {
      setEditHistory((previous) => [...previous.slice(-49), stroke.originalGrid])
    }
    paintStrokeRef.current = null
  }

  const startPanGesture = (event) => {
    const viewport = canvasViewportRef.current
    if (!viewport) return
    panGestureRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
    }
  }

  const movePanGesture = (event) => {
    const viewport = canvasViewportRef.current
    const gesture = panGestureRef.current
    if (!viewport || !gesture || gesture.pointerId !== event.pointerId) return false
    viewport.scrollLeft = gesture.scrollLeft - (event.clientX - gesture.clientX)
    viewport.scrollTop = gesture.scrollTop - (event.clientY - gesture.clientY)
    return true
  }

  const beginPinchGesture = () => {
    const viewport = canvasViewportRef.current
    const points = Array.from(activePointersRef.current.values()).slice(0, 2)
    if (!viewport || points.length < 2) return
    const centerX = (points[0].clientX + points[1].clientX) / 2
    const centerY = (points[0].clientY + points[1].clientY) / 2
    const rect = viewport.getBoundingClientRect()
    pinchGestureRef.current = {
      distance: Math.max(1, Math.hypot(points[0].clientX - points[1].clientX, points[0].clientY - points[1].clientY)),
      zoom: canvasZoomRef.current,
      contentX: (viewport.scrollLeft + centerX - rect.left) / canvasZoomRef.current,
      contentY: (viewport.scrollTop + centerY - rect.top) / canvasZoomRef.current,
    }
  }

  const movePinchGesture = () => {
    const viewport = canvasViewportRef.current
    const gesture = pinchGestureRef.current
    const points = Array.from(activePointersRef.current.values()).slice(0, 2)
    if (!viewport || !gesture || points.length < 2) return

    const centerX = (points[0].clientX + points[1].clientX) / 2
    const centerY = (points[0].clientY + points[1].clientY) / 2
    const distance = Math.max(1, Math.hypot(points[0].clientX - points[1].clientX, points[0].clientY - points[1].clientY))
    const zoom = clampCanvasZoom(gesture.zoom * (distance / gesture.distance))
    const rect = viewport.getBoundingClientRect()

    canvasZoomRef.current = zoom
    setCanvasZoom(zoom)
    requestAnimationFrame(() => {
      viewport.scrollLeft = gesture.contentX * zoom - (centerX - rect.left)
      viewport.scrollTop = gesture.contentY * zoom - (centerY - rect.top)
    })
  }

  const startCanvasPointer = (event) => {
    if (!editMode || !editedPattern || previewEffect !== 'pattern') return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)

    if (event.pointerType === 'touch') {
      activePointersRef.current.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY })
      if (activePointersRef.current.size === 1) {
        touchToolSnapshotRef.current = { pickColorMode, eraseMode, panMode, selectedColorId }
      }
      if (activePointersRef.current.size >= 2) {
        cancelPaintStroke(true)
        const previousTool = touchToolSnapshotRef.current
        if (previousTool) {
          setPickColorMode(previousTool.pickColorMode)
          setEraseMode(previousTool.eraseMode)
          setPanMode(previousTool.panMode)
          setSelectedColorId(previousTool.selectedColorId)
        }
        panGestureRef.current = null
        suppressTouchRef.current = true
        beginPinchGesture()
        return
      }
      if (suppressTouchRef.current) return
    }

    if (panMode) startPanGesture(event)
    else startPaintStroke(event)
  }

  const moveCanvasPointer = (event) => {
    if (!editMode) return
    if (event.pointerType === 'touch' && activePointersRef.current.has(event.pointerId)) {
      activePointersRef.current.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY })
      if (pinchGestureRef.current || activePointersRef.current.size >= 2) {
        event.preventDefault()
        movePinchGesture()
        return
      }
      if (suppressTouchRef.current) return
    }

    if (movePanGesture(event)) {
      event.preventDefault()
      return
    }
    if (paintStrokeRef.current?.pointerId === event.pointerId) {
      event.preventDefault()
      paintStrokeAt(event)
    }
  }

  const finishCanvasPointer = (event) => {
    const wasPinching = Boolean(pinchGestureRef.current)
    if (event.pointerType === 'touch') {
      activePointersRef.current.delete(event.pointerId)
      if (wasPinching || suppressTouchRef.current) {
        event.preventDefault()
        if (activePointersRef.current.size < 2) pinchGestureRef.current = null
        if (activePointersRef.current.size === 0) {
          suppressTouchRef.current = false
          touchToolSnapshotRef.current = null
        }
        panGestureRef.current = null
        return
      }
      if (activePointersRef.current.size === 0) touchToolSnapshotRef.current = null
    }

    if (panGestureRef.current?.pointerId === event.pointerId) panGestureRef.current = null
    finishPaintStroke(event.pointerId)
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const handleCanvasWheel = (event) => {
    if (!editMode) return
    event.preventDefault()
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12
    setCanvasZoomAt(canvasZoomRef.current * factor, event.clientX, event.clientY)
  }
  useEffect(() => {
    const viewport = canvasViewportRef.current
    if (!editMode || !viewport) return undefined
    const onWheel = (event) => handleCanvasWheel(event)
    viewport.addEventListener('wheel', onWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', onWheel)
  }, [editMode])


  const replaceAllBlocks = (targetId) => {
    if (!editedPattern || !batchSourceColorId) return
    if (targetId === batchSourceColorId) return

    let changed = 0
    const newGrid = editedPattern.grid.map((row) => row.map((id) => {
      if (id !== batchSourceColorId) return id
      changed += 1
      return targetId
    }))
    if (!changed) return

    applyEditedGrid(newGrid)
    const remainingIds = editedPattern.legend
      .map((item) => item.id)
      .filter((id) => id !== batchSourceColorId)
    setBatchSourceColorId(targetId || remainingIds[0] || '')
  }

  const getColorName = (colorId) => brand.colors.find((c) => c.id === colorId)?.name || colorId || '空白'

  const confirmBatchReplace = (targetId) => {
    if (!batchSourceColorId || targetId === batchSourceColorId) return
    const fromName = getColorName(batchSourceColorId)
    const toName = targetId ? getColorName(targetId) : '空白'
    if (!window.confirm(`将所有「${fromName}」改为「${toName}」？`)) return
    replaceAllBlocks(targetId)
  }

  const restoringBeadSizeRef = useRef(false)

  // 恢复第二步图片编辑或第三步图纸编辑工作区；兼容旧版仅含 pattern 的草稿。
  const restoreDraft = () => {
    if (!draft || (!draft.image && !draft.pattern)) return

    const targetStep = draft.step === 2 && draft.image ? 2 : 3
    const restoredPattern = targetStep === 3 ? draft.pattern : null
    const restoredEditedPattern = draft.version >= 2
      ? draft.editedPattern
      : restoredPattern
    const restoredEditMode = targetStep === 3 && Boolean(
      draft.version >= 2 ? draft.editMode && restoredEditedPattern : restoredPattern
    )

    restoringBeadSizeRef.current = Boolean(draft.beadSize && draft.brandId && draft.brandId !== brandId)
    setBrandId(draft.brandId || BRAND_OPTIONS[0].id)
    setBoardSize(draft.boardSize || 50)
    setBeadSize(draft.beadSize || getDefaultBeadSize(getBrandBaseId(draft.brandId || BRAND_OPTIONS[0].id)))
    setPatternTitle(draft.patternTitle || '')
    setImage(draft.image || null)
    setImageSource(draft.imageSource || null)
    setPattern(restoredPattern)
    setEditedPattern(restoredEditMode ? restoredEditedPattern : null)
    setEditorState(targetStep === 2 ? draft.editorState || null : null)
    setSaveMode(draft.saveMode || 'public')
    setMirrorExport(Boolean(draft.mirrorExport))
    setMaxColors(draft.maxColors || 36)
    setIsCustomColorCount(Boolean(draft.isCustomColorCount))
    setOptimizeValue(draft.version >= 3 ? draft.optimizeValue ?? 0 : 0)
    setOutline(Boolean(draft.outline))
    setDithering(Boolean(draft.dithering))
    setRemoveBackground(draft.removeBackground ?? true)
    setShowGrid(draft.showGrid ?? true)
    setShowGroupLines(draft.showGroupLines ?? true)
    setShowColorCodes(draft.showColorCodes ?? true)
    setParamsLocked(targetStep === 2 && Boolean(draft.paramsLocked))
    setLiveGridSize(targetStep === 2 ? draft.liveGridSize || null : null)
    setSelectedColorId(restoredEditMode ? draft.selectedColorId || restoredEditedPattern?.legend?.[0]?.id || null : null)
    setBatchSourceColorId(restoredEditMode ? draft.batchSourceColorId || restoredEditedPattern?.legend?.[0]?.id || '' : '')
    setPickColorMode(restoredEditMode && Boolean(draft.pickColorMode))
    setEraseMode(restoredEditMode && Boolean(draft.eraseMode))
    setPanMode(restoredEditMode && Boolean(draft.panMode))
    setBrushSize(BRUSH_SIZES.includes(draft.brushSize) ? draft.brushSize : 1)
    setCanvasZoom(Math.min(MAX_CANVAS_ZOOM, Math.max(MIN_CANVAS_ZOOM, Number(draft.canvasZoom) || 1)))
    setUserWatermark(draft.userWatermark || '')
    setEnableWatermark(Boolean(draft.enableWatermark))
    setPreviewEffect(draft.previewEffect || 'pattern')
    setTextSource(draft.textSource || null)
    setEditHistory([])
    setEditMode(restoredEditMode)
    setStep(targetStep)
  }

  const discardDraft = async () => {
    latestWorkspaceRef.current = null
    await removeWorkspaceDraft(draftKey)
    setDraft(null)
  }

  const brand = findBrandOption(brandId)
  const availableBrands = useMemo(
    () => (isPremium ? BRAND_OPTIONS : BRAND_OPTIONS.filter((o) => o.baseId === 'mard')),
    [isPremium]
  )

  // 非会员只允许 MARD，若当前选中其他品牌则回退
  useEffect(() => {
    if (!availableBrands.find((b) => b.id === brandId)) {
      setBrandId(availableBrands[0]?.id || BRAND_OPTIONS[0].id)
    }
  }, [isPremium, availableBrands, brandId])

  // 用户切换品牌时同步默认粒径；草稿恢复时保留其已选粒径。
  useEffect(() => {
    if (restoringBeadSizeRef.current) {
      restoringBeadSizeRef.current = false
      return
    }
    setBeadSize(getDefaultBeadSize(getBrandBaseId(brandId)))
  }, [brandId])

  // 切换品牌时，把已有 pattern 映射到新色库，避免预览变黑
  useEffect(() => {
    if (!pattern || !brand) return
    const remapped = remapPatternBrand(pattern, brand)
    if (remapped !== pattern) {
      setPattern(remapped)
    }
  }, [brand])

  // 记录已上传的 pattern 对象，避免同一图纸重复上传（PNG/PDF 各导一次）
  const uploadedPatternRef = useRef(null)

  // 生成完成后进入第 3 步（保存/分享改为导出时由用户选择）
  const handlePatternGenerated = async (p, meta) => {
    uploadedPatternRef.current = null
    // meta 存在说明图纸来自文字弹窗，保存配置以支持“重新编辑文字”
    setTextSource(meta || null)
    setPattern(p)
    setEditedPattern(null)
    setEditMode(false)
    setStep(3)
  }

  // 导出时按用户选择保存/分享到图集
  const maybeUploadToGallery = async () => {
    if (!user || !brand || !pattern) return
    if (saveMode === 'none') return
    if (uploadedPatternRef.current === pattern) return
    try {
      const dataUrl = renderPatternToDataURL(pattern, brand)
      const file = dataURItoFile(dataUrl, `doutian_${brand.id}_${pattern.width}x${pattern.height}.png`)
      await galleryApi.upload(file, {
        brand: brand.name,
        width: pattern.width,
        height: pattern.height,
        total: pattern.total,
        pattern: JSON.stringify({ grid: pattern.grid, legend: pattern.legend, width: pattern.width, height: pattern.height, total: pattern.total }),
        isPublic: saveMode === 'public',
        title: patternTitle.trim() || undefined,
      })
      uploadedPatternRef.current = pattern
    } catch (err) {
      console.error('upload to gallery failed', err)
      // 不再静默失败，提示用户保存未成功
      alert(`图纸已导出，但保存到图集失败：${err.response?.data?.error || err.message || '网络异常'}\n可重新导出一次或选择「不保存到图集」。`)
    }
  }

  // 渲染预览网格
  useEffect(() => {
    const activePattern = editedPattern || pattern
    if (!activePattern || !previewCanvasRef.current) return
    const canvas = previewCanvasRef.current
    const ctx = canvas.getContext('2d')

    // 熨烫效果预览：不改变图纸数据，仅换一种渲染方式
    if (previewEffect !== 'pattern') {
      const rendered = renderIroningPreview(activePattern, brand, CANVAS_SCALE, previewEffect)
      canvas.width = rendered.width
      canvas.height = rendered.height
      ctx.drawImage(rendered, 0, 0)
      return
    }

    const { grid, width, height } = activePattern
    canvas.width = width * CANVAS_SCALE
    canvas.height = height * CANVAS_SCALE

    const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))
    const nameMap = new Map(brand.colors.map((c) => [c.id, c.name]))

    // 空格显示为拼豆板底色，不再回退成黑色豆。
    ctx.fillStyle = '#fffdf8'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const hex = colorMap.get(grid[y][x])
        if (!hex) continue
        ctx.fillStyle = hex
        ctx.fillRect(x * CANVAS_SCALE, y * CANVAS_SCALE, CANVAS_SCALE, CANVAS_SCALE)
      }
    }

    // 网格线（每 5×5 一组粗线）——深灰调，避免白色线条让图纸泛白
    if (showGrid) {
      const group = 5
      // 细网格
      ctx.strokeStyle = 'rgba(60, 50, 70, 0.18)'
      ctx.lineWidth = 1
      for (let x = 0; x <= width; x++) {
        ctx.beginPath()
        ctx.moveTo(x * CANVAS_SCALE, 0)
        ctx.lineTo(x * CANVAS_SCALE, height * CANVAS_SCALE)
        ctx.stroke()
      }
      for (let y = 0; y <= height; y++) {
        ctx.beginPath()
        ctx.moveTo(0, y * CANVAS_SCALE)
        ctx.lineTo(width * CANVAS_SCALE, y * CANVAS_SCALE)
        ctx.stroke()
      }
      // 粗线
      ctx.strokeStyle = 'rgba(60, 50, 70, 0.45)'
      ctx.lineWidth = 2
      for (let x = 0; x <= width; x += group) {
        ctx.beginPath()
        ctx.moveTo(x * CANVAS_SCALE, 0)
        ctx.lineTo(x * CANVAS_SCALE, height * CANVAS_SCALE)
        ctx.stroke()
      }
      for (let y = 0; y <= height; y += group) {
        ctx.beginPath()
        ctx.moveTo(0, y * CANVAS_SCALE)
        ctx.lineTo(width * CANVAS_SCALE, y * CANVAS_SCALE)
        ctx.stroke()
      }
    }

    // 色号文字
    if (showColorCodes && CANVAS_SCALE >= MIN_CELL_FOR_CODE) {
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      // 与导出 PNG 的色号字号保持一致（瘦体加粗小号）
      ctx.font = `bold ${Math.max(6, Math.floor(CANVAS_SCALE * 0.24))}px 'Arial Narrow', 'PingFang SC', sans-serif`
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const id = grid[y][x]
          const hex = colorMap.get(id)
          const code = nameMap.get(id) || ''
          if (!hex || !code) continue
          const rgb = hexToRgb(hex)
          // 根据背景亮度决定文字颜色
          const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
          ctx.fillStyle = luminance > 0.5 ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)'
          ctx.fillText(code, x * CANVAS_SCALE + CANVAS_SCALE / 2, y * CANVAS_SCALE + CANVAS_SCALE / 2)
        }
      }
    }

    // 水印始终绘制在图纸 canvas 内，不会进入导出页眉或图例。
    if (!isPremium) {
      const watermark = createPatternWatermarkCanvas(canvas.width, canvas.height, '豆田造物', 'brand')
      ctx.drawImage(watermark, 0, 0, canvas.width, canvas.height)
    }

    if (isPremium && enableWatermark && userWatermark.trim()) {
      const watermark = createPatternWatermarkCanvas(canvas.width, canvas.height, userWatermark.trim(), 'custom')
      ctx.drawImage(watermark, 0, 0, canvas.width, canvas.height)
    }
  }, [pattern, editedPattern, brand, isPremium, showGrid, showColorCodes, enableWatermark, userWatermark, previewEffect])

  // 文件上传处理：先进入裁剪环节
  const [cropSrc, setCropSrc] = useState(null)
  const startImageWorkspace = (nextImage, source) => {
    setImage(nextImage)
    setImageSource(source)
    setPattern(null)
    setEditedPattern(null)
    setEditorState(null)
    setOptimizeValue(0)
    if (source === 'ai') {
      setMaxColors(20)
      setIsCustomColorCount(false)
      setOutline(true)
      setDithering(false)
      setRemoveBackground(true)
    }
    setEditMode(false)
    setEditHistory([])
    setParamsLocked(false)
    setLiveGridSize(null)
    setPickColorMode(false)
    setEraseMode(false)
    setPanMode(false)
    setBrushSize(1)
    setCanvasZoom(1)
    setShowPatternColorTools(false)
    setStep(2)
  }
  const handleFile = (file) => {
    if (!file) return
    setImageUploadError('')

    const isSupportedType = SUPPORTED_LOCAL_IMAGE_TYPES.has(file.type.toLowerCase())
    const hasSupportedName = SUPPORTED_LOCAL_IMAGE_NAME.test(file.name)
    if (!isSupportedType && !(file.type === '' && hasSupportedName)) {
      setImageUploadError('暂不支持该图片格式，请选择 JPG、PNG 或 WebP 图片')
      return
    }
    if (file.size > MAX_LOCAL_IMAGE_BYTES) {
      setImageUploadError('图片不能超过 25 MB，请压缩后重试')
      return
    }

    setImageReading(true)
    const reader = new FileReader()
    reader.onload = (event) => {
      setImageReading(false)
      if (typeof event.target?.result !== 'string') {
        setImageUploadError('图片读取失败，请重新选择')
        return
      }
      setCropSrc(event.target.result)
    }
    reader.onerror = () => {
      setImageReading(false)
      setImageUploadError('图片读取失败，请检查文件后重试')
    }
    reader.onabort = () => {
      setImageReading(false)
      setImageUploadError('图片读取已取消，请重新选择')
    }
    try {
      reader.readAsDataURL(file)
    } catch {
      setImageReading(false)
      setImageUploadError('无法读取该图片，请换一张重试')
    }
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files[0]
    handleFile(file)
  }

  const onPasteImage = (e) => {
    const file = Array.from(e.clipboardData?.files || []).find((item) => item.type.startsWith('image/'))
    if (!file) return
    e.preventDefault()
    handleFile(file)
  }

  // 生图等待期间小贴士轮播（每 4 秒换一条）
  useEffect(() => {
    if (!aiLoading) return
    const t = setInterval(() => setAiTipIndex((i) => (i + 1) % aiTips.length), 4000)
    return () => clearInterval(t)
  }, [aiLoading, aiTips])

  // AI 生图
  const generateAiImage = async () => {
    if (!aiPrompt.trim()) return
    setAiLoading(true)
    setAiTipIndex(0)
    setAiTask({ taskId: null, status: 'pending', imageUrl: null })
    try {
      const refSrcs = [...(aiStyleRef ? [aiStyleRef.src] : []), ...aiBaseImages.map((r) => r.src)]
      let referenceImageUrls = []
      if (refSrcs.length > 0) {
        setAiUploading(true)
        referenceImageUrls = await uploadReferenceImages(refSrcs)
        setAiUploading(false)
      }
      const { data } = await imageApi.generate(aiPrompt.trim(), aiStyle, 512, 512, referenceImageUrls, aiStyleRef ? 1 : 0, 'bead-source')
      const taskId = data.taskId
      setAiTask({ taskId, status: data.status || 'pending', imageUrl: null })
      pollTask(taskId)
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'AI 生图失败')
      setAiLoading(false)
      setAiUploading(false)
      // 生成被拒（额度不足/过期）时同步最新额度，避免页面显示与服务端不一致
      refreshUsage()
    }
  }

  const uploadReferenceImages = async (srcs) => {
    const urls = []
    for (const src of srcs) {
      let file
      if (src.startsWith('data:')) {
        file = dataURItoFile(src, 'reference.png')
      } else {
        const res = await fetch(src, { credentials: 'same-origin' })
        if (!res.ok) throw new Error('无法读取参考图')
        const blob = await res.blob()
        file = new File([blob], 'reference.png', { type: blob.type || 'image/png' })
      }
      const { data } = await imageApi.upload(file)
      urls.push(data.url)
    }
    return urls
  }

  const addAiBaseImage = (src) => {
    if (aiBaseImages.length >= 4) return
    setAiBaseImages((prev) => [...prev, { id: crypto.randomUUID(), src }])
  }

  const removeAiBaseImage = (id) => {
    setAiBaseImages((prev) => prev.filter((r) => r.id !== id))
  }

  const handleAiStyleRefFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => setAiStyleRef({ id: crypto.randomUUID(), src: e.target.result })
    reader.readAsDataURL(file)
    if (aiStyleRefInputRef.current) aiStyleRefInputRef.current.value = ''
  }

  const handleAiBaseFiles = (files) => {
    if (!files) return
    const remaining = 4 - aiBaseImages.length
    const toAdd = Array.from(files).slice(0, remaining)
    toAdd.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => addAiBaseImage(e.target.result)
      reader.readAsDataURL(file)
    })
    if (aiBaseInputRef.current) aiBaseInputRef.current.value = ''
  }

  const useCurrentImageAsBase = () => {
    if (!image || aiBaseImages.length >= 4) return
    addAiBaseImage(image)
  }

  const pollTask = (taskId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    pollIntervalRef.current = setInterval(async () => {
      try {
        const { data } = await imageApi.status(taskId)
        setAiTask({ taskId, status: data.status, imageUrl: data.imageUrl })
        if (data.status === 'completed' || data.status === 'done' || data.imageUrl) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setAiLoading(false)
          // 生成成功已扣减额度，同步刷新保持与服务端一致
          refreshUsage()
          if (data.imageUrl) {
            startImageWorkspace(data.imageUrl, 'ai')
          }
        } else if (data.status === 'failed' || data.status === 'error') {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setAiLoading(false)
          alert('图片生成失败')
        }
      } catch {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
        setAiLoading(false)
      }
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [])

  // 分享链接导入（小红书 / 抖音图文）
  const extractShareUrl = (text) => {
    const match = text.match(
      /(https?:\/\/(?:xhslink\.[a-z]+|www\.xiaohongshu\.com|m\.xiaohongshu\.com|xiaohongshu\.com|v\.douyin\.com|www\.douyin\.com|m\.douyin\.com|douyin\.com|iesdouyin\.com)\/[^\s'"<>]+)/i
    )
    return match ? match[1].replace(/[.,;!?。，；！？]$/, '') : ''
  }

  const doParseXhs = async () => {
    if (!xhsUrl.trim()) return
    const realUrl = extractShareUrl(xhsUrl) || xhsUrl.trim()
    if (!realUrl) {
      alert('未能从文本中提取到分享链接，请检查粘贴内容')
      return
    }
    setXhsLoading(true)
    setXhsImages([])
    try {
      const { data } = await xhsApi.parse(realUrl)
      setXhsTitle(data.title || '分享笔记')
      setXhsImages(data.images || [])
    } catch (err) {
      alert(err.response?.data?.error || err.message || '解析失败，请检查链接')
    } finally {
      setXhsLoading(false)
    }
  }

  const handleParseXhs = () => {
    if (!xhsUrl.trim()) return
    if (!storage.get('xhsDisclaimerAgreed')) {
      setShowXhsDisclaimer(true)
      return
    }
    doParseXhs()
  }

  const agreeXhsDisclaimer = () => {
    storage.set('xhsDisclaimerAgreed', '1')
    setShowXhsDisclaimer(false)
    doParseXhs()
  }

  const selectXhsImage = (url) => {
    startImageWorkspace(xhsApi.proxyUrl(url), 'link')
    setXhsImages([])
  }

  // 导出文件名：优先使用用户填写的图纸名称
  const buildExportName = (ext) => {
    const title = patternTitle.trim().replace(/\s+/g, '_')
    const base = title ? `doutian_${title}` : `doutian_${brand.id}_${pattern.width}x${pattern.height}`
    return `${base}.${ext}`
  }

  // 导出 PNG：带页眉（用户名+品牌）和色号用量图例
  const exportPng = async () => {
    if (!pattern) return
    const hd = !!user?.isPremium
    try {
      await usageApi.export({ type: 'png', hd, width: pattern.width, height: pattern.height })
    } catch (err) {
      alert(err.response?.data?.error || '导出次数已用完，请升级会员~')
      return
    }

    const dataUrl = await exportPatternPng({
      pattern: editedPattern || pattern,
      brand,
      user,
      showGrid,
      showGroupLines,
      showColorCodes,
      beadSize,
      hd,
      watermarkText: enableWatermark ? userWatermark.trim() : null,
      mirrored: mirrorExport,
    })
    await downloadDataUrl(dataUrl, buildExportName('png'))
    refreshUsage()
    maybeUploadToGallery()
  }

  // 导出 PDF：带页眉（用户名+品牌）和色号用量图例
  const exportPdf = async () => {
    if (!pattern) return
    const hd = !!user?.isPremium
    try {
      await usageApi.export({ type: 'pdf', hd, width: pattern.width, height: pattern.height })
    } catch (err) {
      alert(err.response?.data?.error || '导出次数已用完，请升级会员~')
      return
    }

    const pdf = await exportPatternPdf({
      pattern: editedPattern || pattern,
      brand,
      user,
      showGrid,
      showGroupLines,
      showColorCodes,
      maxWidth: limits.maxExportWidth,
      beadSize,
      hd,
      watermarkText: enableWatermark ? userWatermark.trim() : null,
      mirrored: mirrorExport,
    })
    // 统一走 blob 下载，兼容夸克/UC 等移动端浏览器
    downloadBlob(pdf.output('blob'), buildExportName('pdf'))
    refreshUsage()
    maybeUploadToGallery()
  }

  return (
    <div className="space-y-6">
      {/* 标题区 */}
      <section className="relative overflow-hidden rounded-[2rem] bg-creamy p-8 shadow-card md:p-10">
        <span className="pointer-events-none absolute -right-4 -top-4 text-8xl opacity-20">🧁</span>
        <h1 className="relative text-3xl font-extrabold tracking-tight text-cocoa md:text-4xl">
          深耕豆田，粒造万物 ✨
        </h1>
        <p className="relative mt-3 max-w-2xl text-cocoa-light">
          把喜欢的图片丢进豆田，我帮你变成超可爱的拼豆图纸~ 自动匹配品牌色库、计算用量，还能导出高清 PNG/PDF 哦！
        </p>
      </section>

      {/* 三步流程指引 */}
      <section className="card">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          {[
            { step: 1, label: '选择原图', desc: '上传 / AI / 文字 / 链接' },
            { step: 2, label: '编辑调整', desc: '缩放移动 · 锁定参数' },
            { step: 3, label: '生成导出', desc: '参考线 · 色号 · 水印选项' },
          ].map((item, idx) => {
            const active = step === item.step
            const completed = step > item.step
            const reachable = item.step <= step
            return (
              <div key={item.step} className="flex flex-1 items-center">
                <button
                  type="button"
                  disabled={!reachable}
                  onClick={() => reachable && setStep(item.step)}
                  className={`flex-1 rounded-2xl py-2 px-2 text-center transition md:p-4 ${
                    active
                      ? 'bg-blush text-white shadow-soft'
                      : completed
                      ? 'bg-mint/30 text-cocoa'
                      : 'bg-cream-100 text-cocoa-light'
                  } ${reachable ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed opacity-60'}`}
                >
                  <div
                    className={`mx-auto mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold md:h-8 md:w-8 md:text-sm ${
                      active ? 'bg-white text-blush' : completed ? 'bg-mint text-white' : 'bg-lavender/30 text-cocoa-light'
                    }`}
                  >
                    {completed ? '✓' : item.step}
                  </div>
                  <p className="text-[11px] font-semibold md:text-sm">{item.label}</p>
                  <p className={`mt-0.5 hidden text-[10px] md:block ${active ? 'text-white/80' : 'text-cocoa-light'}`}>
                    {item.desc}
                  </p>
                </button>
                {idx < 2 && (
                  <div className="mx-1 h-0.5 w-4 bg-lavender/30 md:mx-2 md:w-8" />
                )}
              </div>
            )
          })}
        </div>
        {step >= 2 && draft?.savedAt && (
          <p className="mt-3 text-right text-[11px] text-cocoa-light">工作区已自动保存</p>
        )}
      </section>

      {/* 第 1 步：选择原图 */}
      {step === 1 && (
      <div className="space-y-6">
      {/* 第二、三步工作区草稿 */}
      {draft && (draft.image || draft.pattern) && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-peach/40 bg-peach-light/40 px-4 py-3">
          <span className="text-sm text-cocoa">
            📝 检测到未完成的{draft.step === 2 && draft.image ? '图片调整' : '图纸编辑'}{draft.pattern ? `（${draft.pattern.width}×${draft.pattern.height}）` : ''}，保存于 {new Date(draft.savedAt).toLocaleString('zh-CN')}
          </span>
          <div className="flex gap-2">
            <button
              onClick={restoreDraft}
              className="rounded-full bg-blush px-4 py-1.5 text-xs font-semibold text-white shadow-soft transition hover:bg-blush-dark"
            >
              继续上次编辑
            </button>
            <button
              onClick={discardDraft}
              className="rounded-full border border-lavender/60 px-3 py-1.5 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
            >
              删除
            </button>
          </div>
        </div>
      )}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blush text-lg">🖼️</span>
          上传图片
        </h2>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onPaste={onPasteImage}
          tabIndex={0}
          role="button"
          aria-busy={imageReading}
          className={`relative rounded-3xl border-2 border-dashed p-6 text-center transition focus:outline-none focus:ring-2 focus:ring-blush/40 ${
            imageReading ? 'cursor-wait opacity-75' : 'cursor-pointer'
          } ${
            dragActive
              ? 'border-blush bg-blush-light/40'
              : 'border-blush/50 bg-white/80 hover:border-blush hover:bg-blush-light/20'
          } shadow-card`}
        >
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blush-light text-3xl shadow-soft">🖼️</div>
          <p className="text-sm font-semibold text-cocoa">
            {imageReading ? '正在读取图片…' : '点击、拖入或粘贴图片'}
          </p>
          <p className="mt-1 text-xs text-cocoa-light">支持 JPG / PNG / WebP，最大 25 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={imageReading}
            aria-label="选择要转换的图片"
            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-wait"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0]
              event.currentTarget.value = ''
              handleFile(file)
            }}
          />
        </div>
        {imageUploadError && (
          <p role="alert" className="mt-2 text-sm text-red-500">{imageUploadError}</p>
        )}
        {!isPremium && (
          <p className="mt-2 text-xs text-cocoa-light">
            免费用户每天可导出 1 张低清带水印图纸，
            <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>
            解锁高清无水印、不限次导出~
          </p>
        )}

        {image && (
          <div className="mt-3 flex items-center gap-3 rounded-2xl border border-lavender/30 bg-white p-3">
            <img src={image} alt="preview" className="h-16 w-16 rounded-xl object-cover" />
            <div className="flex-1">
              <p className="text-sm font-medium text-cocoa">已加载图片</p>
              <p className="text-xs text-cocoa-light">在下方编辑区调整位置后点击「生成图纸」</p>
            </div>
            <button
              onClick={() => { setImage(null); setImageSource(null); setPattern(null) }}
              className="rounded-full px-3 py-1 text-xs text-cocoa-light hover:bg-cream-100"
            >
              移除
            </button>
          </div>
        )}
      </section>

      {/* 文字拼豆入口 */}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-peach text-lg">🔤</span>
          文字拼豆
        </h2>
        <p className="mb-3 text-sm text-cocoa-light">输入文字，一键生成像素字拼豆图纸，支持加边框。</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setTextModalOpen(true)}
            className="btn-soft"
          >
            创建文字图纸
          </button>
          <button
            onClick={() => setOverlayModalOpen(true)}
            className="btn-soft relative"
          >
            叠加文字图纸
            {!isPermanent && <span className="ml-1">🔒</span>}
          </button>
        </div>
      </section>

      {/* AI 生图入口 */}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-lavender text-lg">🎨</span>
          AI 生成拼豆参考图
        </h2>
        {!canUseAi ? (
          <div className="rounded-2xl bg-lavender/20 p-4 text-sm text-cocoa">
            <p>🌱 暂无 AI 生图额度~</p>
            <p className="mt-1 text-cocoa-light">
              去<a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>，
              或每日签到领取额度（首日 1 张，连续 7 天再送 3 张）。
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 md:flex-row">
              <input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="例如：一只戴着草莓帽子的小猫咪 🍓"
                className="form-input flex-1"
                onKeyDown={(e) => e.key === 'Enter' && generateAiImage()}
              />
              <button
                onClick={generateAiImage}
                disabled={aiLoading || aiUploading || !aiPrompt.trim()}
                className={`btn-soft transition ${
                  aiLoading || aiUploading ? 'w-full animate-pulse-soft whitespace-normal text-left text-xs leading-relaxed' : 'shrink-0'
                }`}
              >
                {aiUploading
                  ? '上传垫图…'
                  : aiLoading
                  ? `⏳ 生成中 · ${aiTips[aiTipIndex % aiTips.length]}`
                  : isPremium
                  ? `生成参考图（剩余 ${user?.quota || 0}）`
                  : '生成参考图（今日免费 1 张）'}
              </button>
            </div>
            <div className="text-xs text-cocoa-light">
              {isPremium ? (
                <>
                  AI 额度剩余 {user?.quota || 0} 次
                  {quotaExpiresText ? ` · ${quotaExpiresText} 过期` : ''}
                </>
              ) : (
                <>
                  免费用户每日 1 张 AI 生图 ·{' '}
                  <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>{' '}
                  畅享更多
                </>
              )}
            </div>

            {/* 风格预设 */}
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { key: 'anime', label: '动漫插画', img: '/styles/anime.png', badge: '人像' },
                { key: 'ghibli', label: '吉仆力插画', img: '/styles/ghibli.png', badge: '治愈' },
                { key: 'stardew', label: '田园插画', img: '/styles/stardew.png', badge: '田园' },
                { key: 'chibi', label: 'Q 版像素风', img: '/styles/qban.png', badge: '可爱' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setAiStyle(s.key)}
                  className={`overflow-hidden rounded-2xl border text-left transition ${
                    aiStyle === s.key
                      ? 'border-blush shadow-card ring-2 ring-blush/40'
                      : 'border-lavender/50 bg-white hover:border-blush'
                  }`}
                >
                  <img src={s.img} alt={s.label} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-xs font-semibold text-cocoa">{s.label}</span>
                    <span className="rounded-full bg-cocoa px-1.5 py-0.5 text-[10px] text-white">{s.badge}</span>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setAiStyle('')}
              className="mt-2 text-xs text-cocoa-light hover:text-cocoa"
            >
              不选择风格，使用默认提示词
            </button>

            {/* 参考图（风格，最多 1 张） */}
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-cocoa-light">参考图（风格参考，最多 1 张，选填）</span>
                {!aiStyleRef && (
                  <>
                    <button
                      onClick={() => aiStyleRefInputRef.current?.click()}
                      className="rounded-full border border-lavender/60 px-2.5 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                    >
                      ➕ 上传参考图
                    </button>
                    <input
                      ref={aiStyleRefInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleAiStyleRefFile(e.target.files?.[0])}
                    />
                  </>
                )}
              </div>
              {aiStyleRef ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <div className="relative h-14 w-14 overflow-hidden rounded-xl border border-lavender/30">
                    <img src={aiStyleRef.src} alt="style-ref" className="h-full w-full object-cover" />
                    <button
                      onClick={() => setAiStyleRef(null)}
                      className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cocoa text-[10px] text-white shadow-sm"
                      title="移除"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-xs text-cocoa-light">未选参考图时，以手动选择的风格卡或默认风格生成</p>
              )}
            </div>

            {/* 垫图（内容主体，最多 4 张） */}
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-cocoa-light">垫图（保留指定主体，最多 4 张）</span>
                {aiBaseImages.length < 4 && (
                  <>
                    <button
                      onClick={() => aiBaseInputRef.current?.click()}
                      className="rounded-full border border-lavender/60 px-2.5 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                    >
                      ➕ 上传垫图
                    </button>
                    <input
                      ref={aiBaseInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleAiBaseFiles(e.target.files)}
                    />
                    {image && (
                      <button
                        onClick={useCurrentImageAsBase}
                        className="rounded-full border border-lavender/60 px-2.5 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                      >
                        使用当前原图
                      </button>
                    )}
                  </>
                )}
              </div>

              {aiBaseImages.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {aiBaseImages.map((ref) => (
                    <div key={ref.id} className="relative h-14 w-14 overflow-hidden rounded-xl border border-lavender/30">
                      <img src={ref.src} alt="base" className="h-full w-full object-cover" />
                      <button
                        onClick={() => removeAiBaseImage(ref.id)}
                        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cocoa text-[10px] text-white shadow-sm"
                        title="移除"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-xs text-cocoa-light">人物 / 宠物 / 物品均可；多个主体请在提示词中说明，避免混入无关角色</p>
              )}
            </div>

            {aiTask && (
              <div className="mt-3 text-sm text-cocoa-light">
                任务状态：<span className="font-medium text-blush-dark">{aiTask.status}</span>
                {aiTask.imageUrl && '（已自动加载到生成器）'}
              </div>
            )}
          </>
        )}
      </section>

      {/* 小红书导入 */}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-mint text-lg">📕</span>
          小红书 / 抖音链接导入
        </h2>
        <div className="flex flex-col gap-3 md:flex-row">
          <input
            value={xhsUrl}
            onChange={(e) => setXhsUrl(e.target.value)}
            placeholder="粘贴小红书或抖音分享链接，例如 http://xhslink.cn/... 或 http://v.douyin.com/..."
            className="form-input flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleParseXhs()}
          />
          <button
            onClick={handleParseXhs}
            disabled={xhsLoading || !xhsUrl.trim()}
            className="btn-soft shrink-0"
          >
            {xhsLoading ? '解析中…' : '解析笔记'}
          </button>
        </div>
        <p className="mt-2 text-xs text-cocoa-light">
          解析他人笔记前请征得原作者同意，禁止商用或侵犯版权。
        </p>

        {showXhsDisclaimer && (
          <div className="mt-3 rounded-2xl border border-lavender/40 bg-cream-50/60 p-4">
            <h3 className="text-sm font-bold text-cocoa">分享链接导入免责声明</h3>
            <div className="mt-2 space-y-1 text-xs text-cocoa-light">
              <p>本功能仅作为个人学习、交流便利工具，解析前请确保已征得原作者同意。</p>
              <p>禁止将解析内容用于商业用途、二次分发或任何侵犯原作者版权的行为。</p>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setShowXhsDisclaimer(false)}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
              >
                取消
              </button>
              <button
                onClick={agreeXhsDisclaimer}
                className="rounded-full bg-blush px-3 py-1 text-xs font-semibold text-white shadow-soft transition hover:bg-blush-dark"
              >
                我已阅读并同意
              </button>
            </div>
          </div>
        )}

        {xhsImages.length > 0 && (
          <div className="mt-4 rounded-2xl bg-cream-50/60 p-4">
            <p className="mb-3 text-sm font-semibold text-cocoa">
              识别到 {xhsImages.length} 张图片：{xhsTitle}
            </p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {xhsImages.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => selectXhsImage(url)}
                  className="relative aspect-square overflow-hidden rounded-xl border border-lavender/30 bg-white hover:ring-2 hover:ring-blush"
                >
                  <img
                    src={xhsApi.proxyUrl(url)}
                    alt={`xhs-${idx}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-cocoa-light">点击图片即可加载到生成器~</p>
          </div>
        )}
      </section>
      </div>
      )}

      {/* 第 2 步：编辑调整 */}
      {step === 2 && (
      <div className="grid gap-6 lg:grid-cols-3">
        {/* 左侧控制面板 */}
        <section className="order-2 space-y-4 lg:order-1 lg:col-span-1 lg:sticky lg:top-4 lg:self-start">
          {/* 参数面板 */}
          <div className="card space-y-4">
            <div>
              <label className="mb-1 flex items-center justify-between text-sm font-medium text-cocoa">
                <span>拼豆品牌 / 色库</span>
                {!isPremium && (
                  <a href="/pricing" className="text-xs text-blush-dark hover:underline">
                    升级解锁全部品牌
                  </a>
                )}
              </label>
              <select
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                className="form-input"
              >
                {availableBrands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {!isPremium && (
                <p className="mt-1 text-[10px] text-cocoa-light">
                  免费用户仅可使用 MARD 色库
                </p>
              )}
            </div>

            {/* 画板尺寸滑块已移入「图片编辑」卡片（编辑画板上方） */}

            {/* 图像优化强度：原生像素图高还原模式下跳过优化，不显示 */}
            {!faithful && (
              <div>
                <label className="mb-2 flex items-center justify-between text-sm font-medium text-cocoa">
                  <span>图像优化强度</span>
                  <span className="rounded-full bg-blush px-2 py-0.5 text-xs text-white">
                    {OPTIMIZE_LABEL}
                  </span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={optimizeValue}
                  onChange={(e) => setOptimizeValue(Number(e.target.value))}
                  className="w-full accent-blush"
                />
                <div className="mt-1 flex justify-between text-[10px] text-cocoa-light">
                  <span>弱</span>
                  <span>标准</span>
                  <span>强</span>
                </div>
              </div>
            )}

            {/* 最大颜色数 */}
            <div>
              <label className="mb-2 flex items-center justify-between text-sm font-medium text-cocoa">
                <span>最大颜色数</span>
                <span className="rounded-full bg-blush px-2 py-0.5 text-xs text-white">
                  {maxColors} 色
                </span>
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[12, 20, 36, 64].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setMaxColors(v)
                      setIsCustomColorCount(false)
                    }}
                    className={`rounded-lg border py-1.5 text-xs font-semibold transition ${
                      maxColors === v && !isCustomColorCount
                        ? 'border-blush bg-blush text-white'
                        : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                    }`}
                  >
                    {v}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setIsCustomColorCount(true)}
                  className={`rounded-lg border py-1.5 text-xs font-semibold transition ${
                    isCustomColorCount
                      ? 'border-blush bg-blush text-white'
                      : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                  }`}
                >
                  自定义
                </button>
              </div>
              {isCustomColorCount && (
                <div className="mt-2">
                  <input
                    type="range"
                    min={2}
                    max={150}
                    step={1}
                    value={maxColors}
                    onChange={(e) => setMaxColors(Number(e.target.value))}
                    className="w-full accent-blush"
                  />
                  <div className="mt-1 flex justify-between text-[10px] text-cocoa-light">
                    <span>2 色</span>
                    <span className="font-medium text-blush-dark">当前 {maxColors} 色</span>
                    <span>150 色</span>
                  </div>
                </div>
              )}
            </div>

            {/* 高级选项 */}
            <div className="rounded-2xl border border-lavender/30 bg-cream-50/60">
              <button
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-cocoa"
              >
                <span className="flex items-center gap-2">🌾 高级选项</span>
                <span className="text-cocoa-light transition-transform">{showAdvanced ? '▲' : '▼'}</span>
              </button>
              {showAdvanced && (
                <div className="space-y-4 px-4 pb-4">
                  {faithful ? (
                    <p className="rounded-xl border border-blush/30 bg-blush-light/20 px-3 py-2 text-xs leading-relaxed text-cocoa">
                      ✨ 当前为原生像素图，已启用<b>高还原模式</b>：最大化保留原图色彩与边界，不做描边、合并与杂色清理。
                    </p>
                  ) : (
                    <>
                      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                        <span className="text-sm text-cocoa">自动清除边缘白底</span>
                        <input
                          type="checkbox"
                          checked={removeBackground}
                          onChange={(e) => setRemoveBackground(e.target.checked)}
                          className="h-4 w-4 accent-blush"
                        />
                      </label>
                      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                        <span className="text-sm text-cocoa">自动描边（仅勾勒主体外轮廓）</span>
                        <input
                          type="checkbox"
                          checked={outline}
                          onChange={(e) => setOutline(e.target.checked)}
                          className="h-4 w-4 accent-blush"
                        />
                      </label>
                      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                        <span className="text-sm text-cocoa">柔和过渡（仅平滑区域）</span>
                        <input
                          type="checkbox"
                          checked={dithering}
                          onChange={(e) => setDithering(e.target.checked)}
                          className="h-4 w-4 accent-blush"
                        />
                      </label>
                    </>
                  )}

                  {/* 单粒直径 */}
                  <div>
                    <label className="mb-1 flex items-center justify-between text-xs font-medium text-cocoa-light">
                      <span>单粒直径</span>
                      <span className="rounded-full bg-blush px-2 py-0.5 text-[10px] text-white">{beadSize} mm</span>
                    </label>
                    <div className="flex gap-2">
                      {[2.6, 5, 10].map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={paramsLocked}
                          onClick={() => setBeadSize(s)}
                          className={`flex-1 rounded-lg border py-1 text-xs transition disabled:opacity-50 ${
                            beadSize === s
                              ? 'border-blush bg-blush text-white'
                              : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                          }`}
                        >
                          {s} mm
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 显示网格 */}
                  <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                    <span className="text-sm text-cocoa">显示网格</span>
                    <input
                      type="checkbox"
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                      className="h-4 w-4 accent-blush"
                    />
                  </label>

                  {/* 显示 5×5 辅助线 */}
                  <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                    <span className="text-sm text-cocoa">显示 5×5 辅助线</span>
                    <input
                      type="checkbox"
                      checked={showGroupLines}
                      onChange={(e) => setShowGroupLines(e.target.checked)}
                      className="h-4 w-4 accent-blush"
                    />
                  </label>
                </div>
              )}
            </div>

            {!isPremium && (
              <p className="text-xs leading-relaxed text-cocoa-light">
                小萌新最大支持 {limits.maxBoardSize}×{limits.maxBoardSize}，导出带水印。
                <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级田主会员</a> 解锁高清无水印~
              </p>
            )}
          </div>

        </section>

        {/* 右侧预览区 -- 图片编辑与生成结果 */}
        <section className="order-1 space-y-4 lg:order-2 lg:col-span-2">
          {image && (
            <div className="card">
              <h3 className="mb-1 text-lg font-bold text-cocoa">🖼️ 图片编辑</h3>
              <p className="mb-3 text-xs text-cocoa-light">
                拖拽移动 · 双指捏合缩放 · 滚轮缩放 · 网格 {paramsLocked && liveGridSize ? liveGridSize : isPremium ? boardSize : Math.min(boardSize, limits.maxBoardSize)}×{paramsLocked && liveGridSize ? liveGridSize : isPremium ? boardSize : Math.min(boardSize, limits.maxBoardSize)}
                {paramsLocked && liveGridSize && <span className="ml-1 text-blush-dark">（锁定后实时）</span>}
                {isAiGenerated && <span className="ml-2 rounded-full bg-blush-light/40 px-2 py-0.5 text-blush-dark">AI 拼豆优化</span>}
              </p>
              <BeadEditor
                ref={beadEditorRef}
                src={image}
                gridSize={isPremium ? boardSize : Math.min(boardSize, limits.maxBoardSize)}
                brand={brand}
                maxColors={maxColors}
                dithering={effDithering}
                mergeThreshold={effMerge}
                denoise={effDenoise}
                pixelMode={effPixelMode}
                outline={effOutline}
                faithful={faithful}
                saturation={effSaturation}
                preprocessStrength={effPreprocessStrength}
                removeBackground={effRemoveBackground}
                locked={paramsLocked}
                initialState={editorState}
                onStateChange={setEditorState}
                onGenerate={handlePatternGenerated}
                onGridSizeChange={(size) => setBoardSize(size)}
                onCellEditChange={(editing) => setIsCellEditing(editing)}
                onLiveGridSizeChange={(n) => {
                  setLiveGridSize(n)
                  // 锁定后缩放实时反推画幅规格，同步到上方滑动框
                  if (n) setBoardSize(n)
                }}
                headerControl={
                  <div className="mb-3">
                    <label className="mb-2 flex items-center justify-between text-sm font-medium text-cocoa">
                      <span>画板尺寸</span>
                      <span className="rounded-full bg-blush px-2 py-0.5 text-xs text-white">
                        {boardSize} × {boardSize}（约 {gridToCm(boardSize, beadSize)} × {gridToCm(boardSize, beadSize)} cm）
                      </span>
                    </label>
                    <input
                      type="range"
                      min={10}
                      max={Math.max(limits.maxBoardSize, boardSize)}
                      step={1}
                      value={boardSize}
                      disabled={paramsLocked}
                      onChange={(e) => setBoardSize(Number(e.target.value))}
                      className="w-full accent-blush disabled:opacity-50"
                    />
                    <div className="mt-1 flex justify-between text-[10px] text-cocoa-light">
                      <span>10 × 10</span>
                      <span>{Math.max(limits.maxBoardSize, boardSize)} × {Math.max(limits.maxBoardSize, boardSize)}</span>
                    </div>
                  </div>
                }
                toolbarControl={
                  <button
                    type="button"
                    onClick={() => {
                      if (isCellEditing) {
                        // 单粒编辑模式下，点击锁定按钮 = 应用
                        beadEditorRef.current?.applyCellEdit()
                        setParamsLocked(true)
                      } else {
                        setParamsLocked((v) => {
                          if (v) setLiveGridSize(null)
                          return !v
                        })
                      }
                    }}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                      paramsLocked
                        ? 'border border-lavender/60 bg-cream-200 text-cocoa-muted hover:bg-cream-100'
                        : 'bg-blush text-white shadow-soft hover:bg-blush-dark'
                    }`}
                  >
                    {paramsLocked ? '🔒 已锁定（点击解锁）' : '🔓 锁定画板与豆子尺寸'}
                  </button>
                }
              />
            </div>
          )}

          {step === 2 && !pattern && (
            <div className="flex h-96 flex-col items-center justify-center rounded-[2rem] border-2 border-dashed border-lavender/40 bg-white text-center shadow-card">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blush-light text-5xl shadow-soft">🎀</div>
              <p className="mt-5 text-cocoa-light">调整位置后，点击上方「生成图纸」进入下一步~</p>
            </div>
          )}
        </section>
      </div>
      )}

      {/* 第 3 步：生成导出 */}
      {step === 3 && pattern && (
        <section className="card space-y-4">
          <div className="flex flex-col gap-3 border-b border-lavender/25 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-cocoa">{editMode ? '编辑图纸' : '生成结果'}</h3>
              <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-cocoa-light">
                <span className="rounded-md bg-cream-100 px-2 py-1">{brand.name}</span>
                <span className="rounded-md bg-cream-100 px-2 py-1">{pattern.width}×{pattern.height} 格</span>
                <span className="rounded-md bg-cream-100 px-2 py-1">{(editedPattern || pattern).total} 豆</span>
                <span className="rounded-md bg-cream-100 px-2 py-1">{(editedPattern || pattern).legend.length} 色</span>
                <span className="rounded-md bg-cream-100 px-2 py-1">约 {gridToCm(pattern.width, beadSize)} cm</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!editMode && (
                <>
                  <button type="button" onClick={() => setStep(2)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-lavender/60 bg-white text-cocoa transition hover:bg-lavender/30" aria-label="返回图片调整" title="返回图片调整">
                    <ArrowLeft size={17} aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => {
                      setEditedPattern(structuredClone(pattern))
                      setSelectedColorId(brand.colors[0]?.id || null)
                      setBatchSourceColorId(pattern.legend?.[0]?.id || '')
                      setPickColorMode(false)
                      setEraseMode(false)
                      setPanMode(false)
                      setCanvasZoom(1)
                      setShowPatternColorTools(false)
                      setEditHistory([])
                      setPreviewEffect('pattern')
                      setEditMode(true)
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-lavender/60 bg-white px-3 py-2 text-xs font-semibold text-cocoa transition hover:bg-lavender/30"
                  >
                    <Pencil size={15} aria-hidden="true" />
                    编辑图纸
                  </button>
                  {textSource && (
                    <button
                      onClick={() => {
                        if (textSource.kind === 'overlay') setOverlayModalOpen(true)
                        else setTextModalOpen(true)
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-blush/60 bg-blush/10 px-3 py-2 text-xs font-semibold text-cocoa transition hover:bg-blush/20"
                      title="以原参数重新打开文字弹窗，调整后将重新生成图纸（覆盖当前图纸）"
                    >
                      <Type size={15} aria-hidden="true" />
                      重新编辑文字
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setLocalBeadModePattern(editedPattern || pattern, brand.id)
                      navigate('/bead-mode/local')
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-mint/60 bg-mint/20 px-3 py-2 text-xs font-semibold text-cocoa transition hover:bg-mint/40"
                    title="逐色号高亮辅助拼图（永久会员）"
                  >
                    🧩 拼豆模式
                    {!isPermanent && <span>🔒</span>}
                  </button>
                  <button type="button" onClick={() => document.getElementById('pattern-export')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="flex h-9 w-9 items-center justify-center rounded-lg bg-blush text-white shadow-soft transition hover:bg-blush-dark" aria-label="前往导出" title="前往导出">
                    <Download size={17} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          </div>
          {/* 编辑模式：颜色选择器 */}
          {editMode && (
            <div className="space-y-3 rounded-2xl border border-lavender/30 bg-cream-50/60 p-3 lg:sticky lg:top-4 lg:z-20">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm font-medium text-cocoa">
                  {panMode
                    ? '拖动画布；双指捏合或滚轮缩放'
                    : pickColorMode
                      ? '点击图纸吸取颜色'
                      : eraseMode
                        ? '点击图纸擦除色块'
                        : '选择颜色后点击图纸修改'}
                </span>
                <div className="grid grid-cols-3 gap-2 sm:flex sm:w-auto">
                  <button
                    type="button"
                    onClick={undoEdit}
                    disabled={editHistory.length === 0}
                    className="inline-flex h-9 items-center justify-center gap-1 rounded-lg border border-lavender/60 bg-white px-2 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                    aria-label="撤回上一步编辑"
                    title="撤回上一步编辑"
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    撤回
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editHistory.length > 0 && !window.confirm('放弃本次图纸编辑？已修改的格子不会保留。')) return
                      setPickColorMode(false)
                      setEraseMode(false)
                      setPanMode(false)
                      setShowPatternColorTools(false)
                      setEditHistory([])
                      setEditedPattern(null)
                      setEditMode(false)
                    }}
                    className="inline-flex h-9 items-center justify-center gap-1 rounded-lg border border-lavender/60 bg-white px-2 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                  >
                    <X size={15} aria-hidden="true" />
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editedPattern) {
                        setPattern(editedPattern)
                      }
                      setPickColorMode(false)
                      setEraseMode(false)
                      setPanMode(false)
                      setShowPatternColorTools(false)
                      setEditHistory([])
                      setEditedPattern(null)
                      setEditMode(false)
                    }}
                    className="inline-flex h-9 items-center justify-center gap-1 rounded-lg bg-blush px-2 text-xs font-semibold text-white shadow-soft transition hover:bg-blush-dark"
                  >
                    <Check size={15} aria-hidden="true" />
                    完成
                  </button>
                </div>
              </div>

              {/* 编辑工具 + 当前颜色 */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex overflow-hidden rounded-lg border border-lavender/60 bg-white">
                  <button
                    type="button"
                    aria-pressed={!pickColorMode && !eraseMode && !panMode}
                    onClick={() => {
                      setPickColorMode(false)
                      setEraseMode(false)
                      setPanMode(false)
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${!pickColorMode && !eraseMode && !panMode ? 'bg-blush text-white' : 'text-cocoa hover:bg-lavender/30'}`}
                  >
                    <Paintbrush size={15} aria-hidden="true" />
                    画笔
                  </button>
                  <button
                    type="button"
                    aria-pressed={pickColorMode}
                    onClick={() => {
                      setPickColorMode(true)
                      setEraseMode(false)
                      setPanMode(false)
                    }}
                    className={`flex items-center gap-1.5 border-l border-lavender/40 px-3 py-1.5 text-xs font-medium transition ${pickColorMode ? 'bg-blush text-white' : 'text-cocoa hover:bg-lavender/30'}`}
                  >
                    <Pipette size={15} aria-hidden="true" />
                    吸色
                  </button>
                  <button
                    type="button"
                    aria-pressed={eraseMode}
                    onClick={() => {
                      setPickColorMode(false)
                      setEraseMode(true)
                      setPanMode(false)
                    }}
                    className={`flex items-center gap-1.5 border-l border-lavender/40 px-3 py-1.5 text-xs font-medium transition ${eraseMode ? 'bg-blush text-white' : 'text-cocoa hover:bg-lavender/30'}`}
                  >
                    <Eraser size={15} aria-hidden="true" />
                    橡皮擦
                  </button>
                  <button
                    type="button"
                    aria-pressed={panMode}
                    onClick={() => {
                      setPickColorMode(false)
                      setEraseMode(false)
                      setPanMode(true)
                    }}
                    className={`flex items-center gap-1.5 border-l border-lavender/40 px-3 py-1.5 text-xs font-medium transition ${panMode ? 'bg-blush text-white' : 'text-cocoa hover:bg-lavender/30'}`}
                  >
                    <Hand size={15} aria-hidden="true" />
                    拖动
                  </button>
                </div>
                {!pickColorMode && !panMode && (
                  <div className="inline-flex h-8 items-center overflow-hidden rounded-lg border border-lavender/60 bg-white" aria-label="笔触大小">
                    <button
                      type="button"
                      onClick={() => setBrushSize((size) => BRUSH_SIZES[Math.max(0, BRUSH_SIZES.indexOf(size) - 1)])}
                      disabled={brushSize === BRUSH_SIZES[0]}
                      className="h-8 w-8 text-base font-semibold text-cocoa transition hover:bg-lavender/30 disabled:opacity-35"
                      aria-label="减小笔触"
                      title="减小笔触"
                    >
                      −
                    </button>
                    <output className="w-12 border-x border-lavender/40 text-center text-xs font-semibold text-cocoa" aria-live="polite">
                      {brushSize}×{brushSize}
                    </output>
                    <button
                      type="button"
                      onClick={() => setBrushSize((size) => BRUSH_SIZES[Math.min(BRUSH_SIZES.length - 1, BRUSH_SIZES.indexOf(size) + 1)])}
                      disabled={brushSize === BRUSH_SIZES[BRUSH_SIZES.length - 1]}
                      className="h-8 w-8 text-base font-semibold text-cocoa transition hover:bg-lavender/30 disabled:opacity-35"
                      aria-label="增大笔触"
                      title="增大笔触"
                    >
                      +
                    </button>
                  </div>
                )}
                <div className="inline-flex h-8 items-center overflow-hidden rounded-lg border border-lavender/60 bg-white" aria-label="画布缩放">
                  <button
                    type="button"
                    onClick={() => setCanvasZoomAt(canvasZoomRef.current / 1.2)}
                    disabled={canvasZoom <= MIN_CANVAS_ZOOM}
                    className="flex h-8 w-8 items-center justify-center text-cocoa transition hover:bg-lavender/30 disabled:opacity-35"
                    aria-label="缩小画布"
                    title="缩小画布"
                  >
                    <ZoomOut size={16} aria-hidden="true" />
                  </button>
                  <output className="w-14 border-x border-lavender/40 text-center text-xs font-semibold text-cocoa" aria-live="polite">
                    {Math.round(canvasZoom * 100)}%
                  </output>
                  <button
                    type="button"
                    onClick={() => setCanvasZoomAt(canvasZoomRef.current * 1.2)}
                    disabled={canvasZoom >= MAX_CANVAS_ZOOM}
                    className="flex h-8 w-8 items-center justify-center text-cocoa transition hover:bg-lavender/30 disabled:opacity-35"
                    aria-label="放大画布"
                    title="放大画布"
                  >
                    <ZoomIn size={16} aria-hidden="true" />
                  </button>
                </div>
                {selectedColorId && (
                  <button
                    type="button"
                    onClick={() => setShowPatternColorTools((value) => !value)}
                    aria-expanded={showPatternColorTools}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-lavender/60 bg-white px-2 text-xs text-cocoa-light transition hover:bg-lavender/30"
                  >
                    当前颜色
                    <span
                      className="h-5 w-5 rounded-md border border-black/10 shadow-sm"
                      style={{ backgroundColor: brand.colors.find((c) => c.id === selectedColorId)?.hex || '#fff' }}
                    />
                    <span className="max-w-20 truncate font-medium text-cocoa">
                      {brand.colors.find((c) => c.id === selectedColorId)?.name || selectedColorId}
                    </span>
                    <ChevronDown size={14} className={showPatternColorTools ? 'rotate-180 transition' : 'transition'} aria-hidden="true" />
                  </button>
                )}
                <span className="text-[10px] text-cocoa-light">工作区自动保存 · 可单步撤回</span>
              </div>

              <div className="rounded-lg border border-lavender/40 bg-white">
                <button
                  type="button"
                  onClick={() => setShowPatternColorTools((value) => !value)}
                  aria-expanded={showPatternColorTools}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold text-cocoa transition hover:bg-lavender/20"
                >
                  <span>颜色与批量操作</span>
                  <ChevronDown size={16} className={showPatternColorTools ? 'rotate-180 transition' : 'transition'} aria-hidden="true" />
                </button>
                {showPatternColorTools && (
                  <div className="space-y-3 border-t border-lavender/30 p-2">
                    {/* 按色号批量替换 */}
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-lavender/40 bg-cream-50 p-2">
                <span className="text-xs font-semibold text-cocoa">批量换色</span>
                <select
                  value={batchSourceColorId}
                  onChange={(e) => setBatchSourceColorId(e.target.value)}
                  className="min-w-0 rounded-md border border-lavender/50 bg-cream-50 px-2 py-1.5 text-xs text-cocoa"
                  aria-label="需要替换的来源色号"
                >
                  {(editedPattern?.legend || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}（{item.count}）
                    </option>
                  ))}
                </select>
                <span className="text-cocoa-light" aria-hidden="true">→</span>
                <span className="flex items-center gap-1 text-xs text-cocoa">
                  <span
                    className="h-5 w-5 rounded-md border border-black/10"
                    style={{ backgroundColor: brand.colors.find((c) => c.id === selectedColorId)?.hex || '#fff' }}
                  />
                  {brand.colors.find((c) => c.id === selectedColorId)?.name || '选择目标色'}
                </span>
                <button
                  type="button"
                  disabled={!batchSourceColorId || !selectedColorId || batchSourceColorId === selectedColorId}
                  onClick={() => confirmBatchReplace(selectedColorId)}
                  className="rounded-md bg-blush px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blush-dark disabled:opacity-40"
                >
                  全部替换
                </button>
                <button
                  type="button"
                  disabled={!batchSourceColorId}
                  onClick={() => confirmBatchReplace(null)}
                  className="rounded-md border border-lavender/60 px-3 py-1.5 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-40"
                >
                  清空此色
                </button>
              </div>

                    {/* 品牌色板（含色号） */}
                    <div>
                      <p className="mb-1.5 text-xs text-cocoa-light">{brand.name} 色板</p>
                      <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto">
                  {brand.colors.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setSelectedColorId(c.id)
                        setPickColorMode(false)
                        setEraseMode(false)
                      }}
                      className={`flex w-9 flex-col items-center rounded-md border-2 p-0.5 transition ${
                        selectedColorId === c.id
                          ? 'border-blush bg-blush-light/30 shadow-sm'
                          : 'border-transparent hover:bg-lavender/20'
                      }`}
                      title={c.name}
                    >
                      <span
                        className="h-6 w-full rounded-sm border border-black/10"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span className="mt-0.5 w-full truncate text-center text-[9px] leading-tight text-cocoa-light">
                        {c.name}
                      </span>
                    </button>
                  ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 会员水印 */}
              {isPremium ? (
                <div className="flex items-center gap-3 rounded-xl border border-lavender/30 bg-white px-3 py-2">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enableWatermark}
                      onChange={(e) => setEnableWatermark(e.target.checked)}
                      className="h-4 w-4 accent-blush"
                    />
                    <span className="text-sm text-cocoa">添加水印</span>
                  </label>
                  {enableWatermark && (
                    <input
                      type="text"
                      value={userWatermark}
                      onChange={(e) => setUserWatermark(e.target.value.slice(0, 20))}
                      placeholder="输入水印文字"
                      className="form-input flex-1 text-sm py-1"
                    />
                  )}
                </div>
              ) : (
                <p className="text-xs text-cocoa-light">
                  💡 <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a> 可添加自定义水印
                </p>
              )}
            </div>
          )}

          {/* 预览效果切换：图纸 / 熨烫效果 */}
          {!editMode && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-cocoa-light">预览效果</span>
              {IRONING_EFFECTS.map((e) => (
                <button
                  key={e.key}
                  type="button"
                  onClick={() => setPreviewEffect(e.key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    previewEffect === e.key
                      ? 'border-blush bg-blush text-white shadow-soft'
                      : 'border-lavender/60 bg-white text-cocoa hover:bg-lavender/30'
                  }`}
                >
                  {e.label}
                </button>
              ))}
              {previewEffect !== 'pattern' && (
                <span className="text-[10px] text-cocoa-light">
                  熨烫效果为模拟预览，实际以熨烫后为准
                </span>
              )}
            </div>
          )}

          <div
            ref={canvasViewportRef}
            aria-label={editMode ? '可缩放和拖动的图纸画布' : '图纸预览'}
            className={`overflow-auto overscroll-contain rounded-2xl border border-cream-200 bg-cream-100 p-2 ${
              editMode ? 'max-h-[72vh] touch-none' : ''
            }`}
          >
            <canvas
              ref={previewCanvasRef}
              onPointerDown={startCanvasPointer}
              onPointerMove={moveCanvasPointer}
              onPointerUp={finishCanvasPointer}
              onPointerCancel={finishCanvasPointer}
              onContextMenu={(event) => editMode && event.preventDefault()}
              draggable={false}
              className={`mx-auto block h-auto rounded-xl shadow-sm ${
                editMode
                  ? panMode
                    ? 'cursor-grab select-none touch-none active:cursor-grabbing'
                    : 'cursor-crosshair select-none touch-none'
                  : 'max-w-full'
              }`}
              style={{
                imageRendering: 'pixelated',
                touchAction: editMode ? 'none' : 'auto',
                width: editMode ? `${canvasZoom * 100}%` : undefined,
                maxWidth: editMode ? 'none' : '100%',
              }}
            />
          </div>

          {/* 导出按钮 */}
          {!editMode && (
            <div id="pattern-export" className="space-y-3 rounded-2xl border border-lavender/30 bg-cream-50/60 p-3 scroll-mt-4">
              <p className="text-xs text-cocoa-light">
                {isPremium ? '本月已导出' : '今日已导出'} {user?.exportsUsed || 0} / {user?.exportsLimit || 1} 次
                {!isPremium && (
                  <>
                    {' · '}
                    <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>
                    不限次高清无水印
                  </>
                )}
              </p>

              {/* 图纸名称（选填，用作导出文件名与图集标题） */}
              <div>
                <input
                  type="text"
                  value={patternTitle}
                  onChange={(e) => {
                    // 实时过滤：仅保留中英文、数字和空格
                    const filtered = e.target.value.replace(/[^\u4e00-\u9fa5a-zA-Z0-9\s]/g, '')
                    setPatternTitle(filtered.slice(0, 30))
                  }}
                  maxLength={30}
                  placeholder="图纸名称（选填，用作文件名），例如：草莓小猫"
                  className="form-input text-sm"
                />
              </div>

              {/* 保存/分享选择 */}
              {user && (
                <select
                  value={saveMode}
                  onChange={(e) => setSaveMode(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="none">不保存到图集</option>
                  <option value="private">保存到图集（仅自己可见）</option>
                  <option value="public">保存到图集并公开分享</option>
                </select>
              )}

              {/* 导出选项：显示色号（默认勾选） */}
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                <span className="text-sm text-cocoa">显示色号（导出图纸标注每粒色号）</span>
                <input
                  type="checkbox"
                  checked={showColorCodes}
                  onChange={(e) => setShowColorCodes(e.target.checked)}
                  className="h-4 w-4 accent-blush"
                />
              </label>

              {/* 镜像导出开关 */}
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                <span className="text-sm text-cocoa">镜像导出（图纸左右翻转）</span>
                <input
                  type="checkbox"
                  checked={mirrorExport}
                  onChange={(e) => setMirrorExport(e.target.checked)}
                  className="h-4 w-4 accent-blush"
                />
              </label>

              <div className="flex gap-2">
                <button onClick={exportPng} className="btn-primary flex-1 py-2 text-sm md:py-2.5 md:text-base">
                  导出 PNG 🖼️
                </button>
                <button onClick={exportPdf} className="btn-soft flex-1 py-2 text-sm md:py-2.5 md:text-base">
                  导出 PDF 📄
                </button>
              </div>
            </div>
          )}

          {/* 图例 & 用量 */}
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-cocoa-light">色号图例 & 用量</h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {[...(editedPattern || pattern).legend]
                .sort((a, b) => b.count - a.count)
                .map((item) => {
                  const total = (editedPattern || pattern).total
                  const percent = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0'
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-2xl border border-lavender/30 bg-white p-2 shadow-sm"
                    >
                      <div
                        className="h-8 w-8 rounded-xl border border-black/10 shadow-sm"
                        style={{ backgroundColor: item.hex }}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-cocoa">{item.name}</p>
                        <p className="text-xs text-cocoa-light">× {item.count} · {percent}%</p>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        </section>
      )}

      <TextBeadModal
        open={textModalOpen}
        brand={brand}
        onClose={() => setTextModalOpen(false)}
        onConfirm={handlePatternGenerated}
        initial={textSource?.kind === 'single' ? textSource.config : undefined}
      />

      <TextOverlayModal
        open={overlayModalOpen}
        brand={brand}
        onClose={() => setOverlayModalOpen(false)}
        onConfirm={handlePatternGenerated}
        initial={textSource?.kind === 'overlay' ? textSource.config : undefined}
      />

      <ImageCropModal
        open={!!cropSrc}
        src={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={(dataUrl) => {
          setCropSrc(null)
          startImageWorkspace(dataUrl, 'upload')
        }}
      />
    </div>
  )
}
